import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import type { SelfVoiceData, SelfVoiceAudio } from '@shared/types';
import BusyOverlay from '@/components/BusyOverlay';
import Dropdown from '@/components/Dropdown';
import { pauseSiblingAudios } from '@/lib/pauseSiblingAudios';
import { audioFilenamePreview } from '@/lib/audioFilenamePreview';
import { useFreshnessPoll } from '@/lib/useFreshnessPoll';
import { categoryOptions, dialectOptions, accentOptions, audioLanguageOptions } from '@/pages/admin/audioOptions';
import SelfConflictModal from './SelfConflictModal';
import { useSelfSave } from './useSelfSave';
import { selfApi } from './selfApi';
import { useT } from './i18n/LanguageProvider';
import { localizeLanguageOptions, withEmptyLabel } from './i18n/languageNames';
import { selfFieldLabels } from './selfFieldLabels';
import LangToggle from './LangToggle';
import styles from './Self.module.css';

interface Props {
  token: string;
  initial: SelfVoiceData;
}

export default function SelfEditPage({ token, initial }: Props) {
  // Sprache (Spec 2026-09-15 Sprachumschalter): Umschalten rendert nur neu —
  // Formular und Staging liegen in `useSelfSave` und bleiben unberührt.
  const { lang, t } = useT();
  const fl = selfFieldLabels(lang);
  // Datenstand, Speichern und Konfliktabgleich liegen in `useSelfSave` — hier
  // steht nur noch die Darstellung.
  const {
    data, form, staging, saving, byteProgress, phaseText, dirty, nameDirty, conflict, failures,
    set, addFiles, updateMeta, removeStaged, save, resolveConflict, adoptFresh, adopting,
  } = useSelfSave(token, initial);
  // Aktualitaets-Ping (Spec 2026-09-15 §5–§7): die Seite ist immer im
  // Bearbeiten-Zustand, gepingt wird also durchgehend — nur waehrend des
  // Speicherns (inkl. Uploads) ausgesetzt. Verglichen wird der Fingerabdruck der
  // Self-Sicht auf Ungleichheit (Spec §6): interne TRO-Aenderungen melden nichts.
  const freshness = useFreshnessPoll({
    enabled: true,
    paused: saving,
    currentStamp: data.version,
    fetchStamp: () => selfApi.getStamp(token),
    stampKey: (s) => s.version,
    compare: 'different',
    identity: token,
  });
  // Automatisches Übernehmen (Spec 2026-09-15 §7): im Self-Service gibt es nichts
  // abzuwaegen — kein Banner, kein Knopf. Sobald der Ping einen anderen Stand
  // meldet und gerade nichts anderes laeuft (Speichern, Übernehmen, offener
  // Konflikt-Dialog), wird genau EINMAL je Meldung uebernommen: der Ref merkt
  // sich den gemeldeten Fingerabdruck, weil `adoptFresh` pro Render neu entsteht
  // und der Effekt deshalb oefter laeuft. Danach wird die Meldung verworfen —
  // bei Erfolg ist sie ohnehin ueberholt, bei einem Fehler versucht es der
  // naechste Ping erneut.
  const { newer: frischer, dismiss: meldungVerwerfen } = freshness;
  const uebernommeneMeldung = useRef<string | null>(null);
  useEffect(() => {
    if (!frischer || saving || adopting || conflict) return;
    if (uebernommeneMeldung.current === frischer.version) return;
    uebernommeneMeldung.current = frischer.version;
    void adoptFresh().finally(() => {
      meldungVerwerfen();
      uebernommeneMeldung.current = null;
    });
  }, [frischer, saving, adopting, conflict, adoptFresh, meldungVerwerfen]);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const languageOptions = useMemo(
    () => localizeLanguageOptions(data.languages_list.map((l) => ({ value: l.code, label: l.label })), lang, t.legacyValue),
    [data.languages_list, lang, t.legacyValue],
  );

  function onDrop(e: DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  }

  // Dateinamen-Vorschau: nur solange der Name im Formular vom gespeicherten
  // Stand abweicht — sonst zeigt jede Probe ohnehin ihren aktuellen Namen.
  function ziel(a: SelfVoiceAudio): string | null {
    if (!nameDirty) return null;
    return audioFilenamePreview({
      audio: { ...a, voice_id: data.voice_id, storage_path: a.filename, sort_order: 0, is_external: false, source_url: null, original_filename: null },
      lastName: form.last_name, firstName: form.first_name || null,
    });
  }

  // Lösch-Mail geht an das TRO-Team → Betreff und Text bleiben deutsch; nur die
  // Knopfbeschriftung folgt der Sprache.
  const mailName = `${form.first_name} ${form.last_name}`.trim();
  const mailBody =
    `Hallo TRO,\n\n` +
    `hiermit bitte ich um Löschung von folgenden Sprachproben aus eurer Sprecherdatenbank von mir (${mailName}):\n\n` +
    `[HIER DIE DATEINAMEN DER ZU LÖSCHENDEN SPRACHPROBEN EINTRAGEN]\n\n` +
    `Vielen Dank und freundliche Grüße\n${mailName}`;
  const mailHref =
    `mailto:voices@tro.de?subject=${encodeURIComponent(`Sprachprobe löschen — ${mailName}`)}` +
    `&body=${encodeURIComponent(mailBody)}`;

  // Während Speichern/Upload nicht wegnavigieren (Upload läuft sequenziell).
  useEffect(() => {
    if (!saving) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [saving]);

  return (
    <div className={styles.page}>
      <BusyOverlay active={saving}
        progress={byteProgress ?? undefined}
        locale={lang === 'en' ? 'en-GB' : 'de-DE'}
        text={phaseText ?? t.saving} />
      <div className={styles.header}>
        <img src="/brand/tro_logo_pos.svg" alt="TRO" className={styles.logo} />
        <span className={styles.headerText}>{t.profileTitle}</span>
        <div className={styles.headerLang}>
          <LangToggle />
        </div>
      </div>

      {/* Stammdaten */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t.sectionBasics}</h2>
        <div className={styles.grid2}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-last">{fl.last_name}</label>
            <input id="f-last" className={styles.input} value={form.last_name} onChange={(e) => set('last_name', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-first">{fl.first_name}</label>
            <input id="f-first" className={styles.input} value={form.first_name} onChange={(e) => set('first_name', e.target.value)} />
          </div>
        </div>
        <label className={styles.checkboxRow}>
          <input type="checkbox" checked={form.eigenes_studio} onChange={(e) => set('eigenes_studio', e.target.checked)} />
          {t.ownStudioCheckbox}
        </label>
      </section>

      {/* Kontakt */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t.sectionContact}</h2>
        <div className={styles.grid2}>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-phone">{fl.phone}</label>
            <input id="f-phone" className={styles.input} value={form.phone} onChange={(e) => set('phone', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-landline">{fl.phone_landline}</label>
            <input id="f-landline" className={styles.input} value={form.phone_landline} onChange={(e) => set('phone_landline', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-email">{fl.email}</label>
            <input id="f-email" className={styles.input} type="email" value={form.email} onChange={(e) => set('email', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-web">{fl.web}</label>
            <input id="f-web" className={styles.input} value={form.web} onChange={(e) => set('web', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-strasse">{fl.strasse}</label>
            <input id="f-strasse" className={styles.input} value={form.strasse} onChange={(e) => set('strasse', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-plz">{fl.plz}</label>
            <input id="f-plz" className={styles.input} value={form.plz} onChange={(e) => set('plz', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-ort">{fl.ort}</label>
            <input id="f-ort" className={styles.input} value={form.ort} onChange={(e) => set('ort', e.target.value)} />
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor="f-land">{fl.land}</label>
            <input id="f-land" className={styles.input} value={form.land} onChange={(e) => set('land', e.target.value)} />
          </div>
        </div>
      </section>

      {/* Sprachen / Dialekte / Akzente */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t.sectionLanguages}</h2>
        <div className={styles.field}>
          <span className={styles.label}>{fl.languages}</span>
          <Dropdown multiple options={languageOptions} value={form.languages} onChange={(v) => set('languages', v)} placeholder={t.languagesPlaceholder} />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>{fl.dialects}</span>
          <Dropdown
            multiple
            options={data.vocab.dialects.map((d) => ({ value: d, label: d }))}
            value={form.dialects}
            onChange={(v) => set('dialects', v)}
            lockedValues={data.locked_dialects}
            lockedTitle={t.lockedBySample}
            placeholder={t.dialectsPlaceholder}
          />
        </div>
        <div className={styles.field}>
          <span className={styles.label}>{fl.accents}</span>
          <Dropdown
            multiple
            options={data.vocab.accents.map((a) => ({ value: a, label: a }))}
            value={form.accents}
            onChange={(v) => set('accents', v)}
            lockedValues={data.locked_accents}
            lockedTitle={t.lockedBySample}
            placeholder={t.accentsPlaceholder}
          />
        </div>
      </section>

      {/* Neue Sprachproben */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t.sectionUpload}</h2>
        <div
          className={dragActive ? styles.dropzoneActive : styles.dropzone}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={onDrop}
        >
          {t.dropzone}
        </div>
        <input
          ref={fileInputRef}
          className={styles.hiddenInput}
          type="file"
          accept="audio/*,.m4a,.wav,.aac,.flac,.ogg,.opus,.aif,.aiff"
          multiple
          onChange={(e) => { addFiles(Array.from(e.target.files ?? [])); e.target.value = ''; }}
        />
        {staging.map((st) => (
          <div key={st.id} className={styles.stageRow}>
            <div className={styles.stageHead}>
              <span className={styles.stageName}>{st.file.name}</span>
              <button type="button" className={styles.btnRemove} onClick={() => removeStaged(st.id)}>{t.removeStaged}</button>
            </div>
            <div className={styles.stageGrid}>
              <div className={styles.field}>
                <span className={styles.label}>{t.metaCategory}</span>
                <Dropdown options={withEmptyLabel(categoryOptions(data.vocab.categories), t.noCategory)} value={st.meta.category ?? ''} onChange={(v) => updateMeta(st.id, { category: v || null })} />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>{t.metaLanguage}</span>
                <Dropdown options={localizeLanguageOptions(audioLanguageOptions(st.meta.language), lang, t.legacyValue)} value={st.meta.language} onChange={(v) => updateMeta(st.id, { language: v })} />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>{t.metaDialect}</span>
                <Dropdown options={withEmptyLabel(dialectOptions(data.vocab.dialects), t.noDialect)} value={st.meta.dialect ?? ''} onChange={(v) => updateMeta(st.id, { dialect: v || null })} />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>{t.metaAccent}</span>
                <Dropdown options={withEmptyLabel(accentOptions(data.vocab.accents), t.noAccent)} value={st.meta.accent ?? ''} onChange={(v) => updateMeta(st.id, { accent: v || null })} />
              </div>
              <div className={styles.field}>
                <span className={styles.label}>{t.metaFreetext}</span>
                <input
                  className={styles.input}
                  type="text"
                  maxLength={20}
                  value={st.meta.freetext_suffix ?? ''}
                  placeholder={t.freetextPlaceholder}
                  onChange={(e) => updateMeta(st.id, { freetext_suffix: e.target.value || null })}
                />
              </div>
            </div>
            <label className={styles.checkboxRow}>
              <input type="checkbox" checked={st.meta.is_demomix} onChange={(e) => updateMeta(st.id, { is_demomix: e.target.checked })} />
              {t.demomix}
            </label>
          </div>
        ))}
      </section>

      {/* Vorhandene Sprachproben — nach dem Upload-Bereich */}
      <section className={styles.card}>
        <h2 className={styles.cardTitle}>{t.sectionSamples}</h2>
        {failures.length > 0 && (
          <div className={styles.failBanner} role="alert">
            <p>{t.renameFailuresHeading(failures.length)}</p>
            <ul>
              {failures.map((f) => (
                <li key={f.id}>{f.filename}{f.target ? ` → ${f.target}` : ''}: {t.failureReason(f.error)}</li>
              ))}
            </ul>
            <button type="button" className={styles.btnGhost} disabled={saving} onClick={() => void save()}>{t.retry}</button>
          </div>
        )}
        {data.audios.length === 0 && <p className={styles.audioMeta}>{t.noSamples}</p>}
        {data.audios.map((a) => {
          const target = ziel(a);
          const renaming = target !== null && target !== a.filename;
          return (
            <div key={a.id} className={renaming ? styles.audioRowRename : styles.audioRow}>
              {renaming
                ? <span className={styles.audioTarget}>{target}</span>
                : <span className={styles.audioName}>{a.filename}</span>}
              {renaming && <span className={styles.renameHint}>{t.renameHint}</span>}
              <audio controls preload="none" className={styles.audioPlayer} onPlay={pauseSiblingAudios} src={selfApi.audioUrl(token, a.filename)} />
            </div>
          );
        })}
        <p className={styles.mailHint}>{t.mailHint}</p>
        <a className={styles.mailLink} href={mailHref}>
          <span className={styles.btnDanger}>{t.requestDeletion}</span>
        </a>
      </section>

      <SelfConflictModal conflict={conflict} busy={saving} onResolve={resolveConflict} />

      {dirty && (
        <div className={styles.saveBar}>
          <button type="button" className={styles.btnPrimary} disabled={saving || adopting} onClick={() => void save()}>
            {saving ? t.savingButton : t.saveButton}
          </button>
        </div>
      )}
    </div>
  );
}
